<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Delivery terms"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasDeliveryTermsData"
            :has-errors="hasSectionErrors"
          />
          Delivery terms
        </span>
      </template>

      <validation-observer>
        <b-row v-if="parcel.delivery_terms">
          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="INCOTERM code"
              rules="required"
            >
              <b-form-group>
                <label>
                  INCOTERM code <span class="text-danger">*</span>
                  <small class="text-muted">1401035000</small>
                </label>
                <v-select
                  v-model="parcel.delivery_terms.trade_terms_condition"
                  :options="incotermOptions"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Delivery location name"
              rules="required"
            >
              <b-form-group>
                <label>
                  Delivery location name <span class="text-danger">*</span>
                  <small class="text-muted">1401037000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Name of the delivery location'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="parcel.delivery_terms.trade_terms_location_name"
                  v-b-tooltip.hover.top="getIssueMessage('delivery_terms.trade_terms_location_name')"
                  :class="getIssueClass('delivery_terms.trade_terms_location_name')"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Country"
              rules="required"
            >
              <b-form-group>
                <label>
                  Country <span class="text-danger">*</span>
                  <small class="text-muted">1401020000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Country of delivery location'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <v-select
                  v-model="parcel.delivery_terms.trade_terms_country"
                  :options="countriesOptions"
                  :reduce="option => option.value"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Delivery terms description"
            >
              <b-form-group>
                <label>
                  Delivery terms description
                  <small class="text-muted">1401009000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Description of delivery terms'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="parcel.delivery_terms.trade_terms_text"
                  v-b-tooltip.hover.top="getIssueMessage('delivery_terms.trade_terms_text')"
                  :class="getIssueClass('delivery_terms.trade_terms_text')"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>
      </validation-observer>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    vSelect,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    countriesOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      incotermOptions: [
        { label: 'EXW - Ex Works', value: 'EXW' },
        { label: 'FCA - Free Carrier', value: 'FCA' },
        { label: 'CPT - Carriage Paid To', value: 'CPT' },
        { label: 'CIP - Carriage and Insurance Paid To', value: 'CIP' },
        { label: 'DAP - Delivered At Place', value: 'DAP' },
        { label: 'DPU - Delivered at Place Unloaded', value: 'DPU' },
        { label: 'DDP - Delivered Duty Paid', value: 'DDP' },
        { label: 'FAS - Free Alongside Ship', value: 'FAS' },
        { label: 'FOB - Free On Board', value: 'FOB' },
        { label: 'CFR - Cost and Freight', value: 'CFR' },
        { label: 'CIF - Cost, Insurance and Freight', value: 'CIF' },
      ],
    }
  },
  computed: {
    hasDeliveryTermsData() {
      if (!this.parcel || !this.parcel.delivery_terms) {
        return false
      }
      const {
        trade_terms_condition: tradeTermsCondition,
        trade_terms_location_name: tradeTermsLocationName,
        trade_terms_country: tradeTermsCountry,
        trade_terms_text: tradeTermsText,
      } = this.parcel.delivery_terms
      return !!(
        tradeTermsCondition
        || tradeTermsLocationName
        || tradeTermsCountry
        || tradeTermsText
      )
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('delivery_terms.'))
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
